import { useState, useCallback, useRef, useEffect } from 'react';
import { DEMO_RESPONSES } from '../data/demoResponses';

const TYPING_DELAY = 300;
const THINKING_DELAY = 800;
const HIGHLIGHT_DURATION = 3000;
const INTER_STEP_PAUSE = 1500;
const SCROLL_SETTLE = 200;

const STEP_HIGHLIGHTS = [
  'MALAT1 appears across 8 diseases — strongest signal in lung cancer',
  'Dual-ranking combines expression + network scores for consensus targets',
  '72.5% rescue score — silencing reverses most disease dysregulation',
  'Orange nodes = FDA drug targets with actionable connections',
];

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  visualization?: any;
  timestamp: Date;
  streaming?: boolean;
}

interface UseGuidedDemoOptions {
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  clearChat: () => void;
  scrollToMessage: (messageId: string) => void;
}

interface UseGuidedDemoReturn {
  isDemoRunning: boolean;
  demoStep: number;
  highlightMsgId: string | null;
  highlightText: string | null;
  startDemo: () => void;
  exitDemo: () => void;
}

export function useGuidedDemo({
  setMessages,
  clearChat,
  scrollToMessage,
}: UseGuidedDemoOptions): UseGuidedDemoReturn {
  const [isDemoRunning, setIsDemoRunning] = useState(false);
  const [demoStep, setDemoStep] = useState(-1);
  const [highlightMsgId, setHighlightMsgId] = useState<string | null>(null);
  const [highlightText, setHighlightText] = useState<string | null>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const scrollRef = useRef(scrollToMessage);
  scrollRef.current = scrollToMessage;

  const clearTimers = useCallback(() => {
    for (const t of timersRef.current) {
      clearTimeout(t);
    }
    timersRef.current = [];
  }, []);

  const addTimer = useCallback((fn: () => void, delay: number) => {
    const id = setTimeout(fn, delay);
    timersRef.current = [...timersRef.current, id];
    return id;
  }, []);

  const runStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= DEMO_RESPONSES.length) {
        setDemoStep(DEMO_RESPONSES.length);
        setHighlightMsgId(null);
        setHighlightText(null);
        return;
      }

      const step = DEMO_RESPONSES[stepIndex];
      setDemoStep(stepIndex);
      setHighlightMsgId(null);
      setHighlightText(null);

      // Phase 1: Add user message
      const userMsg: Message = {
        id: `demo-user-${stepIndex}`,
        role: 'user',
        content: step.userQuery,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMsg]);

      // Phase 2: Thinking pause, then show assistant response
      addTimer(() => {
        const assistantMsg: Message = {
          id: `demo-assistant-${stepIndex}`,
          role: 'assistant',
          content: step.assistantText,
          visualization: step.visualization,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, assistantMsg]);

        // Phase 3: Scroll into view, then show highlight
        addTimer(() => {
          scrollRef.current(`demo-assistant-${stepIndex}`);

          addTimer(() => {
            setHighlightMsgId(`demo-assistant-${stepIndex}`);
            setHighlightText(STEP_HIGHLIGHTS[stepIndex] ?? null);

            // Phase 4: Hold highlight, then advance
            addTimer(() => {
              setHighlightMsgId(null);
              setHighlightText(null);

              // Pause before next step
              addTimer(() => {
                runStep(stepIndex + 1);
              }, INTER_STEP_PAUSE);
            }, HIGHLIGHT_DURATION);
          }, SCROLL_SETTLE);
        }, SCROLL_SETTLE);
      }, THINKING_DELAY);
    },
    [addTimer, setMessages],
  );

  const startDemo = useCallback(() => {
    clearChat();
    clearTimers();
    setIsDemoRunning(true);
    setDemoStep(0);
    addTimer(() => runStep(0), TYPING_DELAY);
  }, [clearChat, clearTimers, addTimer, runStep]);

  const exitDemo = useCallback(() => {
    clearTimers();
    clearChat();
    setIsDemoRunning(false);
    setDemoStep(-1);
    setHighlightMsgId(null);
    setHighlightText(null);
  }, [clearTimers, clearChat]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    isDemoRunning,
    demoStep,
    highlightMsgId,
    highlightText,
    startDemo,
    exitDemo,
  };
}
